import { Injectable } from '@angular/core';
import { DataLayerInterface } from './data-layer.interface';
import { DataLayerService } from './data-layer.service';
import { CartItem } from '../components/abstract-item-row/abstract-item-row';
import { OfferModel } from '../models/payment/offer.model';
import { CartRowsUpdaterService } from '../../cart/services/cart-rows-updater.service';
import { GooglePatternNumberFormatter } from '../services/google-pattern-number-formatter';

@Injectable()
export class GtmDataLayer implements DataLayerInterface {
    protected dataLayerName = 'dataLayer';
    protected brandExtraField = 'brand';
    protected variantExtraFields = [];
    protected freight;
    protected paymentMethod;
    protected items = [];

    constructor(
        protected cartRowsUpdaterService: CartRowsUpdaterService,
        protected dataLayer: DataLayerService,
        protected googlePatternNumberFormatter: GooglePatternNumberFormatter
    ) {
        if (this.isEventsShouldBeEmitted()) {
            this.configure();
            this.dataLayer.removed$.subscribe((cartItem: CartItem) => {
                this.emitCartItemRemoved(cartItem);
            });
            this.dataLayer.selectFreight$.subscribe(freight => {
                this.freight = freight;
                this.emitInitCheckout();
            });
            this.dataLayer.selectPaymentMethod$.subscribe(paymentMethod => {
                this.paymentMethod = paymentMethod;
                if (null !== this.paymentMethod) {
                    this.emitInitPayment();
                }
            });
            this.dataLayer.finalized$.subscribe((offers: Array<OfferModel>) => {
                this.emitFinalized(offers);
            });
            this.cartRowsUpdaterService.indexed$.subscribe(items => this.items = items);
        }
    }

    /**
     * Events should be emitted when gtmDataLayer flag is configured as
     * true in window context.
     **/
    public isEventsShouldBeEmitted() {
        return window.hasOwnProperty('gtmDataLayer') && !!window['gtmDataLayer'];
    }
    public emitCartItemRemoved(cartItem: CartItem) {
        window[this.dataLayerName].push({
            'event': 'removeFromCart',
            'ecommerce': {
                'remove': {
                    'products': [{
                        'name': cartItem.name,
                        'id': cartItem.id,
                        'price': cartItem.price,
                        'brand': this.findCartItemBrand(cartItem),
                        'variant': this.findCartItemVariant(cartItem),
                        'category': cartItem.categories.map(category => {
                            return category.name;
                        }).join(' / '),
                        'quantity': cartItem.quantity
                    }]
                }
            }
        });
    }
    public emitInitCheckout() {
        window[this.dataLayerName].push({
            'event': 'checkout',
            'ecommerce': {
                'checkout': {
                    'actionField': {'step': 1, 'option': this.freight.description},
                    'products': this.items.map(cartItem => {
                        return {
                            'name': cartItem.name,
                            'id': cartItem.id,
                            'price': cartItem.price,
                            'brand': this.findCartItemBrand(cartItem),
                            'variant': this.findCartItemVariant(cartItem),
                            'category': cartItem.categories.map(category => {
                                return category.name;
                            }).join(' / ')
                        };
                    })
                }
            }
        });
    }
    public emitInitPayment() {
        window[this.dataLayerName].push({
            'event': 'checkout',
            'ecommerce': {
                'checkout': {
                    'actionField': {'step': 2, 'option': this.paymentMethod.name},
                    'products': this.items.map(cartItem => {
                        return {
                            'name': cartItem.name,
                            'id': cartItem.id,
                            'price': cartItem.price,
                            'brand': this.findCartItemBrand(cartItem),
                            'variant': this.findCartItemVariant(cartItem),
                            'category': cartItem.categories.map(category => {
                                return category.name;
                            }).join(' / ')
                        };
                    })
                }
            }
        });
    }
    public emitFinalized(offers: Array<OfferModel>) {
        for (const offer of offers) {
            window[this.dataLayerName].push({
                'event' : 'purchase',
                'ecommerce': {
                    'purchase': {
                        'actionField': {
                            'id': offer.id,
                            'affiliation': window.location.host,
                            'revenue': this.googlePatternNumberFormatter.transform(offer.total),
                            'shipping': this.googlePatternNumberFormatter.transform(offer.freight)
                        },
                        'products': this.buildProductsArr(offer)
                    }
                },
                'email': offer.client.email,
                'deadline': offer.deliveries.zip_code_range.deadline
            });
        }
    }

    /**
     * Capture from window global variable possible configuration values.
     **/
    private configure() {
        if (window.hasOwnProperty('gtmDataLayerName')) {
            this.dataLayerName = window['gtmDataLayerName'];
        }
        if (window.hasOwnProperty('gtmDataLayerBrandExtraField')) {
            this.brandExtraField = window['gtmDataLayerBrandExtraField'];
        }
        if (window.hasOwnProperty('gtmDataLayerVariationExtraFields')) {
            this.variantExtraFields = window['gtmDataLayerVariationExtraFields'].split(',');
        }
    }
    /**
     * Find cart item brand extra field value.
     **/
    private findCartItemBrand(cartItem: CartItem) {
        if (!cartItem.extra_fields.hasOwnProperty(this.brandExtraField)) {
            return '';
        }

        return cartItem.extra_fields[this.brandExtraField].values.map(brand => {
            return brand.value;
        }).join(',');
    }
    /**
     * Find cart item variant extra fields values.
     **/
    private findCartItemVariant(cartItem: CartItem) {
        return this.variantExtraFields.map(variation => {
            if (!cartItem.extra_fields.hasOwnProperty(variation)) {
                return '';
            }

            return cartItem.extra_fields[variation].values.map(brand => {
                return brand.value;
            }).join(',');
        }).join(',');
    }
    /**
     * Build products array considering offer's items or plan.
     **/
    private buildProductsArr(offer) {
        return this.findOfferItems(offer).map(item => {
            if (offer.plan !== undefined) {
                return {
                    'name': item.description,
                    'id': item.id,
                    'price': this.googlePatternNumberFormatter.transform(item.amount),
                    'brand': this.findCartItemBrand(item),
                    'variant': this.findCartItemVariant(item),
                    'category': '',
                    'quantity': 1
                };
            }

            return {
                'name': item.item.name,
                'id': item.item.id,
                'price': this.googlePatternNumberFormatter.transform(item.unit_price),
                'brand': this.findCartItemBrand(item.item),
                'variant': this.findCartItemVariant(item.item),
                'category': item.item.categories.map(category => {
                    return category.name;
                }).join(' / '),
                'quantity': parseFloat(item.quantity)
            };
        })
    }
    /**
     * Find items from offer.
     **/
    private findOfferItems(offer) {
        if (offer.plan !== undefined) {
            return [offer.plan];
        }

        return offer.items;
    }
}



// WEBPACK FOOTER //
// /codebuild/output/src4078178155/src/src/app/shared/data-layer/gtm.data-layer.ts